import { useEffect, useState } from "react"
import { Loader2, RotateCw } from "lucide-react"
import type { OtkDestructionAct } from "shared"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScenarioNotice } from "@/components/ScenarioNotice"
import { Skeleton } from "@/components/ui/skeleton"
import { Status } from "@/components/Status"
import { ActPrintSheet } from "@/components/warehouse/ActPrintSheet"
import { PunktValue } from "@/components/warehouse/PunktValue"
import { retryDestructionAct } from "@/lib/api/destructionActs"
import { useDestructionAct } from "@/hooks/useDestructionAct"
import {
  destructionActRetryErrorMessage,
  destructionActStatusLabels,
  pendingActFromError,
} from "@/lib/destructionAct"
import { formatKyivTime } from "@/lib/documents"
import { formatCommissionMember, formatUaDate } from "@/lib/warehouse"
import { useReadOnly, useRolePermissions } from "@/store/session"

/**
 * Re-renders once `submitRetryAt` passes, so the retry button enables while
 * the dialog stays open — same approach as `UnsentDocumentActions`.
 */
function useRetryAvailable(submitRetryAt: string | undefined): boolean {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const available = submitRetryAt === undefined || Date.parse(submitRetryAt) <= nowMs
  useEffect(() => {
    if (available || submitRetryAt === undefined) return
    const delay = Date.parse(submitRetryAt) - Date.now()
    const timer = setTimeout(() => setNowMs(Date.now()), Math.max(delay, 0) + 50)
    return () => clearTimeout(timer)
  }, [available, submitRetryAt])
  return available
}

/**
 * design-system.md's `ActViewDialog` — read-only reprint of an act. Used
 * both from this page's «Знищені» tab (act-number link) and
 * warehouse-acts.md's own archive. A `SUBMISSION_PENDING` act additionally
 * offers «Повторити відправку» (`POST /otk/destruction-acts/:id/retry`,
 * customer decision 30.09.2026) once its `submitRetryAt` has passed;
 * `onActChanged` lets the caller project the result onto its own list. Resolves the act on demand against the
 * real `GET /otk/destruction-acts/:id` (`useDestructionAct`) rather than
 * looking it up in a locally cached list — the act's own `items` already
 * embed series/number, so there is no separate blanks lookup either.
 */
export function ActViewDialog({
  actId,
  onActChanged,
  onOpenChange,
}: {
  actId: string | null
  onActChanged?: (act: OtkDestructionAct) => void
  onOpenChange: (open: boolean) => void
}) {
  const { act, isLoading, error, replaceAct } = useDestructionAct(actId)
  const readOnly = useReadOnly()
  const { manageWarehouseDistribution } = useRolePermissions()
  const pending = act?.status === "SUBMISSION_PENDING"
  const retryAvailable = useRetryAvailable(pending ? act?.submitRetryAt : undefined)
  const canRetry = pending && !readOnly && manageWarehouseDistribution
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<{ actId: string; message: string } | null>(null)

  async function handleRetry() {
    if (!act) return
    setRetrying(true)
    setRetryError(null)
    try {
      const updated = await retryDestructionAct(act.id)
      replaceAct(updated)
      onActChanged?.(updated)
    } catch (err) {
      const current = pendingActFromError(err)
      if (current) {
        replaceAct(current)
        onActChanged?.(current)
      }
      setRetryError({ actId: act.id, message: destructionActRetryErrorMessage(err) })
    } finally {
      setRetrying(false)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={actId !== null}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Акт знищення спецпродукції{act ? ` № ${act.actNumber}` : ""}
          </DialogTitle>
        </DialogHeader>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        )}
        {error && <ScenarioNotice tone="danger">{error}</ScenarioNotice>}
        {act && (
          <div className="grid max-h-[70vh] gap-2 overflow-y-auto text-sm">
            <p className="flex items-center gap-2">
              Статус: <Status>{destructionActStatusLabels[act.status] ?? act.status}</Status>
            </p>
            {pending && (
              <ScenarioNotice tone="warning">
                Акт підписано й збережено, але ЄДРТЗ ще не підтвердив його
                прийняття. Обрані бланки закріплено за цим актом і до
                прийняття вони не переходять у «Знищені». Повторна відправка
                надсилає той самий підписаний акт — новий підпис не потрібен.
                {!retryAvailable && act.submitRetryAt && (
                  <> Повторна відправка буде доступна о {formatKyivTime(act.submitRetryAt)}.</>
                )}
              </ScenarioNotice>
            )}
            {retryError?.actId === act.id && (
              <ScenarioNotice tone="danger">{retryError.message}</ScenarioNotice>
            )}
            <p>Дата складання: {formatUaDate(act.actDate)}</p>
            <p>Місце складання: {act.location || "—"}</p>
            <p>
              Період накопичення: {formatUaDate(act.dateFrom)}–
              {formatUaDate(act.dateTo)}
            </p>
            <p>
              Голова комісії: {formatCommissionMember(act.commissionChair) || "—"}
            </p>
            {act.commissionMembers.length > 0 && (
              <p>
                Члени комісії:{" "}
                {act.commissionMembers
                  .map((m) => formatCommissionMember(m))
                  .join("; ")}
              </p>
            )}
            {(act.destructionPlace || act.destructionTime || act.destructionMethod) && (
              <p className="mt-2 font-medium">Місце, час і спосіб знищення</p>
            )}
            {act.destructionPlace && <p>Місце: {act.destructionPlace}</p>}
            {act.destructionTime && <p>Час: {act.destructionTime}</p>}
            {act.destructionMethod && <p>Спосіб: {act.destructionMethod}</p>}
            <div className="mt-2 max-h-64 overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60">
                  <tr>
                    <th className="p-2 text-left font-medium">Серія і номер</th>
                    <th className="p-2 text-left font-medium">
                      Причина знищення
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {act.items.map((item) => (
                    <tr className="border-t" key={item.blankId}>
                      <td className="p-2 font-medium">
                        {item.series} {item.number}
                      </td>
                      <td className="p-2">
                        {act.blankReasons[item.blankId] ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground">
              До офіційного акта до кожного рядка вклеюється вирізаний з
              бланка номер — цього АРМ не замінює.
            </p>
            <div className="max-h-64 overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60">
                  <tr>
                    <th className="p-2 text-left font-medium">Серія і номер</th>
                    <th className="p-2 text-left font-medium">Пункт</th>
                  </tr>
                </thead>
                <tbody>
                  {act.items.map((item) => (
                    <tr className="border-t" key={item.blankId}>
                      <td className="p-2 font-medium">
                        {item.series} {item.number}
                      </td>
                      {/* `OtkBlank`/`DestructionActItem` carry no пункт field
                          yet — see `PunktValue`'s own gap note — so every row
                          renders its "not distributed" state today. */}
                      <td className="p-2">
                        <PunktValue punkt={null} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {act.attestation && (
              <div className="mt-2 rounded-md border p-3">
                <p className="font-medium">Підтвердження оператора</p>
                <p className="text-muted-foreground">
                  {act.attestation.byUser}, {formatUaDate(act.attestation.at)}
                </p>
                <p className="mt-1">{act.attestation.statement}</p>
              </div>
            )}
            {act.copyToTerritorialBody && (
              <div className="mt-2 rounded-md border p-3">
                <p className="font-medium">Копія акта територіальному органу</p>
                <p>{act.copyToTerritorialBody.territorialBody}</p>
                <p className="text-muted-foreground">
                  Обов'язок за п. 12 Постанови КМУ № 146 зафіксовано (
                  {act.copyToTerritorialBody.byUser}). АРМ каналу до
                  територіального органу не має — надсилання копії
                  відбувається поза системою, і цей запис його не засвідчує.
                </p>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {canRetry && (
            <Button disabled={retrying || !retryAvailable} onClick={() => void handleRetry()}>
              {retrying ? (
                <Loader2 className="animate-spin" data-icon="inline-start" />
              ) : (
                <RotateCw data-icon="inline-start" />
              )}
              {retrying ? "Відправлення…" : "Повторити відправку"}
            </Button>
          )}
          <Button disabled={!act} onClick={() => window.print()} variant="outline">
            Друкувати / Зберегти PDF
          </Button>
        </DialogFooter>
      </DialogContent>
      {act && (
        <ActPrintSheet
          actDate={act.actDate}
          actNumber={act.actNumber}
          attestationStatement={act.attestation.statement}
          attestedByUser={act.attestation.byUser}
          blankReasons={act.blankReasons}
          blanks={act.items.map((item) => ({
            id: item.blankId,
            series: item.series,
            number: item.number,
          }))}
          commissionChair={act.commissionChair}
          commissionMembers={act.commissionMembers}
          dateFrom={act.dateFrom}
          dateTo={act.dateTo}
          destructionMethod={act.destructionMethod}
          destructionPlace={act.destructionPlace}
          destructionTime={act.destructionTime}
          location={act.location}
          territorialBody={act.copyToTerritorialBody.territorialBody}
        />
      )}
    </Dialog>
  )
}
